if (!customElements.get('size-variant-picker')) {
  class SizeVariantPicker extends HTMLElement {
    connectedCallback() {
      this.fieldset = this.parentElement?.querySelector('[data-size-source]');
      if (!this.fieldset || this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.radios = Array.from(this.fieldset.querySelectorAll('input[type="radio"]'));
      this.prices = {};
      const priceNode = this.querySelector('[data-size-prices]');
      if (priceNode) {
        try {
          this.prices = JSON.parse(priceNode.textContent);
        } catch (error) {
          this.prices = {};
        }
      }

      const parsed = this.radios.map((radio) => this.parseSize(radio.value));
      const canUseMatrix = this.dataset.mode === 'matrix' && parsed.length > 0 && parsed.every(Boolean);
      if (canUseMatrix) {
        this.buildMatrix(parsed);
      } else {
        this.buildSearch();
      }
      this.fieldset.addEventListener('change', () => this.syncFromRadio());
      this.syncFromRadio();
      this.fieldset.classList.add('visually-hidden');
      this.fieldset.setAttribute('aria-hidden', 'true');
    }

    parseSize(value) {
      const match = String(value).trim().match(/^(\d+(?:\.\d+)?)ft x (\d+(?:\.\d+)?)ft$/i);
      const widthIndex = Number(this.dataset.widthIndex);
      if (!match || (widthIndex !== 1 && widthIndex !== 2)) return null;
      const first = Number(match[1]);
      const second = Number(match[2]);
      return {
        width: widthIndex === 1 ? first : second,
        length: widthIndex === 1 ? second : first,
        raw: value,
      };
    }

    labelFor(value) {
      const price = this.prices[value];
      return price ? `${value} — ${price}` : value;
    }

    checkedRadio() {
      return this.radios.find((radio) => radio.checked && radio.dataset.quoteSize !== 'true') || this.radios.find((radio) => radio.dataset.quoteSize !== 'true') || this.radios[0];
    }

    selectValue(value) {
      const radio = this.radios.find((item) => item.value === value);
      if (!radio || radio.checked) {
        this.syncFromRadio();
        return;
      }
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      this.syncFromRadio();
    }

    syncFromRadio() {
      const checked = this.checkedRadio();
      if (!checked) return;
      if (this.filterInput && document.activeElement !== this.filterInput) {
        this.filterInput.value = checked.value;
      }
      this.querySelectorAll('[data-value]').forEach((button) => {
        button.setAttribute('aria-selected', button.dataset.value === checked.value ? 'true' : 'false');
      });
      const parsed = this.parseSize(checked.value);
      if (!parsed || !this.widthSelect || !this.lengthSelect) return;
      this.widthSelect.value = String(parsed.width);
      this.fillLengths(parsed.width, parsed.length);
    }

    buildSearch() {
      const search = this.querySelector('[data-search]');
      const matrix = this.querySelector('[data-matrix]');
      if (matrix) matrix.hidden = true;
      if (search) search.hidden = false;
      const list = this.querySelector('[data-size-list]');
      this.filterInput = this.querySelector('[data-size-filter]');
      if (!list || !this.filterInput) return;

      list.replaceChildren();
      this.radios.forEach((radio) => {
        if (radio.dataset.quoteSize === 'true') return;
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.value = radio.value;
        button.textContent = this.labelFor(radio.value);
        button.setAttribute('role', 'option');
        if (radio.classList.contains('disabled')) button.dataset.unavailable = 'true';
        button.addEventListener('click', () => {
          this.selectValue(radio.value);
          list.hidden = true;
          this.filterInput.setAttribute('aria-expanded', 'false');
        });
        item.append(button);
        list.append(item);
      });

      const showList = () => {
        list.hidden = false;
        this.filterInput.setAttribute('aria-expanded', 'true');
        this.applyFilter();
      };
      this.filterInput.addEventListener('change', (event) => event.stopPropagation());
      this.filterInput.addEventListener('focus', () => {
        showList();
        this.filterInput.select();
      });
      this.filterInput.addEventListener('blur', () => {
        window.setTimeout(() => {
          if (document.activeElement === this.filterInput) return;
          const checked = this.checkedRadio();
          if (checked) this.filterInput.value = checked.value;
        }, 150);
      });
      this.filterInput.addEventListener('input', (event) => {
        event.stopPropagation();
        showList();
      });
      this.filterInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          const visible = list.querySelector('li:not([hidden]) button');
          if (visible) visible.click();
        }
        if (event.key === 'Escape') {
          list.hidden = true;
          this.filterInput.setAttribute('aria-expanded', 'false');
        }
      });
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) {
          list.hidden = true;
          this.filterInput.setAttribute('aria-expanded', 'false');
        }
      });
    }

    applyFilter() {
      const selected = (this.checkedRadio()?.value || '').trim().toLowerCase();
      const query = this.filterInput.value.trim().toLowerCase();
      const filtering = query !== '' && query !== selected;
      this.querySelectorAll('[data-size-list] li').forEach((item) => {
        const button = item.querySelector('button');
        const text = button?.textContent?.toLowerCase() || '';
        item.hidden = filtering && !text.includes(query);
      });
    }

    buildMatrix(parsed) {
      const matrix = this.querySelector('[data-matrix]');
      const search = this.querySelector('[data-search]');
      if (search) search.hidden = true;
      if (!matrix) return;
      matrix.hidden = false;

      const widths = [...new Set(parsed.map((size) => size.width))].sort((a, b) => a - b);
      this.parsedSizes = parsed;
      matrix.replaceChildren();

      const caret = () => {
        const icon = document.createElement('span');
        icon.className = 'svg-wrapper';
        icon.setAttribute('aria-hidden', 'true');
        const source = this.querySelector('.select .svg-wrapper');
        if (source) icon.innerHTML = source.innerHTML;
        return icon;
      };
      const labeledSelect = (text, select) => {
        const label = document.createElement('label');
        label.className = 'form__label';
        label.append(document.createTextNode(text));
        const box = document.createElement('div');
        box.className = 'select';
        box.append(select, caret());
        label.append(box);
        return label;
      };

      this.widthSelect = document.createElement('select');
      this.widthSelect.className = 'select__select';
      widths.forEach((width) => {
        const option = document.createElement('option');
        option.value = String(width);
        option.textContent = `${width} ft`;
        this.widthSelect.append(option);
      });

      this.lengthSelect = document.createElement('select');
      this.lengthSelect.className = 'select__select';
      const widthLabel = labeledSelect('Width', this.widthSelect);
      const lengthLabel = labeledSelect('Length', this.lengthSelect);

      const row = document.createElement('div');
      row.className = 'size-variant-picker__matrix';
      row.append(widthLabel, lengthLabel);
      matrix.append(row);

      this.widthSelect.addEventListener('change', (event) => {
        event.stopPropagation();
        const width = Number(this.widthSelect.value);
        const current = this.parseSize(this.checkedRadio()?.value || '');
        const keepLength = current && this.parsedSizes.some((size) => size.width === width && size.length === current.length);
        this.fillLengths(width, keepLength ? current.length : null);
        this.selectCurrentMatrix();
      });
      this.lengthSelect.addEventListener('change', (event) => {
        event.stopPropagation();
        this.selectCurrentMatrix();
      });
    }

    fillLengths(width, preferredLength) {
      const lengths = this.parsedSizes
        .filter((size) => size.width === width)
        .map((size) => size.length)
        .filter((length, index, all) => all.indexOf(length) === index)
        .sort((a, b) => a - b);
      this.lengthSelect.replaceChildren();
      lengths.forEach((length) => {
        const option = document.createElement('option');
        option.value = String(length);
        option.textContent = `${length} ft`;
        this.lengthSelect.append(option);
      });
      if (preferredLength != null && lengths.includes(preferredLength)) {
        this.lengthSelect.value = String(preferredLength);
      }
    }

    selectCurrentMatrix() {
      const width = Number(this.widthSelect.value);
      const length = Number(this.lengthSelect.value);
      const match = this.parsedSizes.find((size) => size.width === width && size.length === length);
      if (match) this.selectValue(match.raw);
    }
  }

  customElements.define('size-variant-picker', SizeVariantPicker);
}

function releaseOtherCustomSize() {
  const quoteTitles = ['Other Custom Size', 'Other Custom Sizes'];
  const isQuoteTitle = (value) => quoteTitles.includes(String(value || '').trim());
  document.querySelectorAll('variant-selects').forEach((root) => {
    root.querySelectorAll('[data-quote-size]').forEach((input) => {
      input.checked = false;
    });
    let variant = null;
    try {
      variant = JSON.parse(root.querySelector('[data-selected-variant]')?.textContent || 'null');
    } catch (error) {
      variant = null;
    }
    const selectedQuote = [variant?.option1, variant?.option2, variant?.option3, variant?.title].some(isQuoteTitle);
    if (!selectedQuote) return;
    const next = root.querySelector('input[type="radio"]:checked:not([data-quote-size])')
      || root.querySelector('input[type="radio"]:not([data-quote-size])');
    if (!next) return;
    next.checked = true;
    next.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', releaseOtherCustomSize);
} else {
  releaseOtherCustomSize();
}
